import { Button } from "../ui/button";
import { ArrowUp, ArrowDown, Edit2, Trash2, Music, BookOpen, Film, ExternalLink } from "lucide-react";
import type { HobbyItem } from "./useHobbiesManager";

interface HobbyRowActionsProps {
  hobby: HobbyItem;
  idx: number;
  totalCount: number;
  onEdit: (hobby: HobbyItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function HobbyRowActions({
  hobby,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: HobbyRowActionsProps) {
  return (
    <>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === totalCount - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(hobby)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(hobby.id)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );
}

function getCategoryBadge(category: string) {
  let icon = null;
  let label = category;
  if (category === "music") {
    icon = <Music className="h-3.5 w-3.5" />;
    label = "Music";
  } else if (category === "books") {
    icon = <BookOpen className="h-3.5 w-3.5" />;
    label = "Books";
  } else if (category === "films") {
    icon = <Film className="h-3.5 w-3.5" />;
    label = "Films";
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase font-bold text-brand-secondary bg-brand-secondary/5 px-2 py-0.5 rounded border border-brand-secondary/10">
      {icon}
      {label}
    </span>
  );
}

interface HobbyRowProps {
  hobby: HobbyItem;
  idx: number;
  totalCount: number;
  onEdit: (hobby: HobbyItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function HobbyRow({
  hobby,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: HobbyRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 w-28">
        {getCategoryBadge(hobby.category)}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-semibold text-foreground leading-snug">
          {hobby.title_en}
        </p>
        {hobby.title_tr && hobby.title_tr !== hobby.title_en && (
          <p className="text-xs text-muted-foreground italic">
            {hobby.title_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="text-xs font-semibold text-brand-secondary">
          {hobby.subtitle_en}
        </p>
        {hobby.subtitle_tr && hobby.subtitle_tr !== hobby.subtitle_en && (
          <p className="text-[11px] text-muted-foreground italic">
            {hobby.subtitle_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-xs">
        {hobby.link ? (
          <a
            href={hobby.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-xs text-brand-primary hover:underline font-mono truncate max-w-[200px]"
          >
            <span className="truncate max-w-[150px]">{hobby.link}</span>
            <ExternalLink className="h-3 w-3 ml-1 shrink-0" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </td>
      <HobbyRowActions
        hobby={hobby}
        idx={idx}
        totalCount={totalCount}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
    </tr>
  );
}

interface HobbiesListProps {
  hobbies: HobbyItem[];
  onMove: (index: number, direction: "up" | "down") => void;
  onEdit: (hobby: HobbyItem) => void;
  onDelete: (id: string) => void;
}

export function HobbiesList({ hobbies, onMove, onEdit, onDelete }: HobbiesListProps) {
  if (hobbies.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No hobby items configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4 w-28">Category</th>
              <th className="p-4">Title (EN / TR)</th>
              <th className="p-4">Subtitle/Artist (EN / TR)</th>
              <th className="p-4">Link</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {hobbies.map((hobby, idx) => (
              <HobbyRow
                key={hobby.id}
                hobby={hobby}
                idx={idx}
                totalCount={hobbies.length}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default HobbiesList;

